import { create } from 'zustand';
import type { ToastMessage, TabType } from '../api/types';

interface ToastState {
  message: string;
  type: 'success' | 'error' | 'info';
}

interface UIState {
  // Toast notifications
  toasts: ToastMessage[];
  toast: ToastState | null;
  
  // Loading states
  isLoading: boolean;
  loadingMessage: string;
  
  // Current tab
  currentTab: TabType;
  
  // Navigation
  navigationStack: string[];
  pageTitle: string;
  showBackButton: boolean;
  
  // Modal states
  isPairingModalOpen: boolean;
  isScriptEditorOpen: boolean;
  isSettingsOpen: boolean;
  
  // Actions
  addToast: (message: string, type?: 'success' | 'error' | 'info') => void;
  removeToast: (id: string) => void;
  showToast: (message: string, type?: 'success' | 'error' | 'info') => void;
  hideToast: () => void;
  
  showLoading: (message?: string) => void;
  hideLoading: () => void;
  
  setCurrentTab: (tab: TabType) => void;
  
  pushNavigation: (route: string, title: string) => void;
  popNavigation: () => string | undefined;
  resetNavigation: () => void;
  setPageTitle: (title: string) => void;
  setShowBackButton: (show: boolean) => void;
  
  openPairingModal: () => void;
  closePairingModal: () => void;
  
  openScriptEditor: () => void;
  closeScriptEditor: () => void;
  
  openSettings: () => void;
  closeSettings: () => void;
}

export const useUIStore = create<UIState>((set, get) => ({
  // Initial state
  toasts: [],
  toast: null,
  isLoading: false,
  loadingMessage: '',
  currentTab: 'devices',
  navigationStack: [],
  pageTitle: 'Устройства',
  showBackButton: false,
  isPairingModalOpen: false,
  isScriptEditorOpen: false,
  isSettingsOpen: false,

  // Toast actions
  addToast: (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const id = Math.random().toString(36).substring(2, 9);
    const toast: ToastMessage = { id, message, type };
    
    set(state => ({ 
      toasts: [...state.toasts, toast] 
    }));
    
    // Auto remove after 3 seconds
    setTimeout(() => {
      get().removeToast(id);
    }, 3000);
  },

  removeToast: (id: string) => {
    set(state => ({
      toasts: state.toasts.filter(t => t.id !== id)
    }));
  },

  showToast: (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    set({ toast: { message, type } });
    
    // Auto hide after 3 seconds
    setTimeout(() => {
      set({ toast: null });
    }, 3000);
  },

  hideToast: () => {
    set({ toast: null });
  },

  // Loading actions
  showLoading: (message: string = 'Загрузка...') => {
    set({ isLoading: true, loadingMessage: message });
  },

  hideLoading: () => {
    set({ isLoading: false, loadingMessage: '' });
  },

  // Tab actions
  setCurrentTab: (tab: TabType) => {
    const titles = {
      devices: 'Устройства',
      scripts: 'Сценарии',
      safety: 'Безопасность'
    };
    
    set({ 
      currentTab: tab,
      pageTitle: titles[tab],
      showBackButton: false,
      navigationStack: []
    });
  },

  // Navigation actions
  pushNavigation: (route: string, title: string) => {
    set(state => ({
      navigationStack: [...state.navigationStack, route],
      pageTitle: title,
      showBackButton: true
    }));
  },

  popNavigation: () => {
    const { navigationStack } = get();
    if (navigationStack.length === 0) return undefined;
    
    const newStack = navigationStack.slice(0, -1);
    const titles = {
      devices: 'Устройства',
      scripts: 'Сценарии',
      safety: 'Безопасность'
    };
    const { currentTab } = get();
    
    set({ 
      navigationStack: newStack,
      pageTitle: newStack.length === 0 ? titles[currentTab] : get().pageTitle,
      showBackButton: newStack.length > 0
    });
    
    return navigationStack[navigationStack.length - 1];
  },

  resetNavigation: () => {
    const titles = {
      devices: 'Устройства',
      scripts: 'Сценарии',
      safety: 'Безопасность'
    };
    const { currentTab } = get();
    
    set({
      navigationStack: [],
      pageTitle: titles[currentTab],
      showBackButton: false
    });
  },

  setPageTitle: (title: string) => {
    set({ pageTitle: title });
  },

  setShowBackButton: (show: boolean) => {
    set({ showBackButton: show });
  },

  // Modal actions
  openPairingModal: () => {
    set({ isPairingModalOpen: true });
  },

  closePairingModal: () => {
    set({ isPairingModalOpen: false });
  },

  openScriptEditor: () => {
    set({ isScriptEditorOpen: true });
  },

  closeScriptEditor: () => {
    set({ isScriptEditorOpen: false });
  },

  openSettings: () => {
    set({ isSettingsOpen: true });
  },

  closeSettings: () => {
    set({ isSettingsOpen: false });
  }
}));
